/**
 * Copies `value` to the clipboard. Returns whether the write succeeded.
 *
 * A failure is not an error to throw: some in-app webviews simply have no Clipboard API, and the
 * caller then falls back to selecting the text so the player can copy it themselves. Silently
 * claiming success when the write did not happen is how a player pastes the previous clipboard
 * contents into a banking app.
 */
export async function copyToClipboard(value: string): Promise<boolean> {
  if (value === '') return false;

  const clipboard = navigator.clipboard;
  try {
    await clipboard.writeText(value);
    return true;
  } catch {
    // Permission denied, insecure context, or a webview that exposes the API and then rejects.
  }

  return copyWithExecCommand(value);
}

/**
 * Last-resort path for browsers that still honour `document.execCommand('copy')`. Returns false
 * rather than throwing so the UI can fall through to "select this and copy".
 *
 * `execCommand` is deprecated; it is also the only remaining copy path in several in-app webviews
 * our players actually arrive from. The deprecation is accepted here and nowhere else.
 */
function copyWithExecCommand(value: string): boolean {
  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.setAttribute('aria-hidden', 'true');
  textarea.tabIndex = -1;
  textarea.style.setProperty('position', 'fixed');
  textarea.style.setProperty('top', '0');
  textarea.style.setProperty('inset-inline-start', '0');
  textarea.style.setProperty('opacity', '0');
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  let ok = false;
  try {
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- see comment above
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  textarea.remove();
  return ok;
}
